import React, { useState, ChangeEvent } from 'react';
import { Button, Typography } from '@navi/web-ui/lib/primitives';
import { RadioGroup } from '@navi/web-ui/lib/components';
import { StrategyType, AllocationDpdFilter, StrategyFilterPopperProps } from '../types';
import { DPD_OPTIONS, STRATEGY_OPTIONS, getFilterDisplayLabel } from '../constants';
import ChangeStrategyConfirmModal from './ChangeStrategyConfirmModal';
import { ArrowDownSolidIcon } from '@navi/web-ui/lib/icons';
import { Popper, PopperTrigger, PopperContent } from '@cp/src/components/Popper/Popper';

const StrategyFilterPopper: React.FC<StrategyFilterPopperProps> = ({
  currentDpdFilter,
  currentStrategy,
  onApply,
  showConfirmation = true
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const [selectedDpd, setSelectedDpd] = useState<AllocationDpdFilter>(currentDpdFilter);
  const [selectedStrategy, setSelectedStrategy] = useState<StrategyType>(currentStrategy);
  const [showConfirmModal, setShowConfirmModal] = useState(false);
  const [isApplying, setIsApplying] = useState(false);

  const hasSelectionChanged =
    selectedDpd !== currentDpdFilter || selectedStrategy !== currentStrategy;

  const handleApply = () => {
    if (hasSelectionChanged && showConfirmation) {
      setIsApplying(true);
      setIsOpen(false);
      setShowConfirmModal(true);
    } else {
      setIsOpen(false);
      onApply(selectedDpd, selectedStrategy);
    }
  };

  const handleConfirmChange = () => {
    setShowConfirmModal(false);
    setIsApplying(false);
    onApply(selectedDpd, selectedStrategy);
  };

  const handleCancelConfirm = () => {
    setShowConfirmModal(false);
    setIsApplying(false);
    setSelectedDpd(currentDpdFilter);
    setSelectedStrategy(currentStrategy);
  };

  const handleCancel = () => {
    setIsOpen(false);
    setSelectedDpd(currentDpdFilter);
    setSelectedStrategy(currentStrategy);
  };

  const handleOpenChange = (open: boolean) => {
    setIsOpen(open);
    if (!open && !isApplying) {
      setSelectedDpd(currentDpdFilter);
      setSelectedStrategy(currentStrategy);
    }
  };

  return (
    <>
      <Popper
        placement="top-end"
        open={isOpen}
        onOpenChange={handleOpenChange}
        mainAxisOffset={8}
        crossAxisOffset={0}
      >
        <PopperTrigger>
          <Button
            variant="text"
            endAdornment={<ArrowDownSolidIcon width={8} height={6} color="var(--blue-base)" />}
            className="flex items-center gap-2 px-2 border border-solid border-[var(--blue-border)] !text-sm"
          >
            {getFilterDisplayLabel(currentDpdFilter, currentStrategy)}
          </Button>
        </PopperTrigger>

        <PopperContent
          noHeader
          className="bg-[var(--bg-primary)] rounded-lg border border-[var(--greyscale-light)] min-w-[340px] z-[var(--z-index_max)]"
          style={{ boxShadow: 'var(--box-shadow-popper)' }}
          arrowColor="var(--bg-primary)"
        >
          <div className="px-4 pt-4 pb-2">
            <Typography variant="p3" color="var(--grayscale-3)">
              Select customer cohort
            </Typography>
          </div>

          <div className="pb-3">
            <div className="grid grid-cols-2 gap-3 border-0 border-y border-solid border-[var(--greyscale-light)]">
              <div className="px-4 border-0 border-r border-solid border-[var(--greyscale-light)]">
                <RadioGroup
                  direction="column"
                  name="dpd-filter-popper"
                  value={selectedDpd}
                  options={DPD_OPTIONS}
                  onChange={(e: ChangeEvent<HTMLInputElement>) =>
                    setSelectedDpd(e.target.value as AllocationDpdFilter)
                  }
                  className="gap-2"
                />
              </div>

              <div className="px-4">
                <RadioGroup
                  direction="column"
                  name="strategy-filter-popper"
                  value={selectedStrategy}
                  options={STRATEGY_OPTIONS}
                  onChange={(e: ChangeEvent<HTMLInputElement>) =>
                    setSelectedStrategy(e.target.value as StrategyType)
                  }
                  className="gap-2"
                />
              </div>
            </div>
          </div>

          <div className="px-4 pt-2 pb-4 flex gap-2">
            <Button variant="secondary" size="small" className="flex-1" onClick={handleCancel}>
              Cancel
            </Button>
            <Button variant="primary" size="small" className="flex-1" onClick={handleApply}>
              Apply
            </Button>
          </div>
        </PopperContent>
      </Popper>

      <ChangeStrategyConfirmModal
        isOpen={showConfirmModal}
        targetDpdFilter={selectedDpd}
        targetStrategy={selectedStrategy}
        onConfirm={handleConfirmChange}
        onCancel={handleCancelConfirm}
      />
    </>
  );
};

export default StrategyFilterPopper;
